import { ReactNode } from "react";
import { Skeleton, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import i18nPackage from "../../i18n/i18n";
import { CoreSpinner } from "../../core";
import { BasicStep } from "../components/basic-step";
import { BaseSubstepProps, BasicSubstep, BasicSubstepHeader, SubstepStatus } from "../components/basic-substep";
import Guide from "../components/guide";
import styles from "./index.module.css";
import Check from "../../images/checkmark.svg?react";

type MobileConnectSubstepProps = BaseSubstepProps & {
	recordVideo?: boolean;
	restrictDevice?: boolean;
	isConnected: boolean;
	isVideoSelected?: boolean;
	isLocked?: boolean;
	qrCodeUrl?: string;
};

type ConnectStepStatus = "unstarted" | "started" | "completed";

function RestrictDeviceGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.guideDescription")}
			src="https://demo.video.yuja.com/V/Video?v=14648910&node=63114454&a=79883083"
			iframeTitle={i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.videoTitle")}
			title={i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.guideTitle")}
		/>
	);
}

function ConnectStepMarker({ status }: { status: ConnectStepStatus }) {
	switch (status) {
		case "unstarted":
			return <div className={styles.point} />;
		case "started":
			return <CoreSpinner size={20} />;
		case "completed":
			return <Check />;
	}
}

function ConnectStep({ index, status, children }: { index: number; status: ConnectStepStatus; children: ReactNode }) {
	return (
		<li className={styles.step}>
			<div className={styles.stepLabel}>
				<ConnectStepMarker status={status} />
				<div className={styles.stepNumber}>
					{i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.step", { number: index + 1 })}
				</div>
			</div>
			{children}
		</li>
	);
}

export function MobileConnectSubstep({
	recordVideo = false,
	restrictDevice = false,
	status,
	isConnected = false,
	isVideoSelected = false,
	isLocked = false,
	qrCodeUrl,
	rootRef,
}: MobileConnectSubstepProps) {
	let modeKey: "record" | "restrict" | "restrictAndRecord";
	if (recordVideo) {
		modeKey = restrictDevice ? "restrictAndRecord" : "record";
	} else {
		modeKey = "restrict";
	}
	const statusKey = status === "completed" ? status : "unstarted";

	let scanStepStatus: ConnectStepStatus;
	if (!isConnected) {
		scanStepStatus = "unstarted";
	} else if (recordVideo) {
		scanStepStatus = isVideoSelected ? "completed" : "started";
	} else {
		scanStepStatus = "completed";
	}

	let restrictStepStatus: Exclude<SubstepStatus, "failed">;
	if (scanStepStatus === "completed") {
		restrictStepStatus = isLocked ? "completed" : "started";
	} else {
		restrictStepStatus = "unstarted";
	}

	let scanStepText: string;
	if (scanStepStatus === "completed") {
		scanStepText = i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.steps.scan.completed");
	} else {
		scanStepText = i18nPackage.t(`beforeQuiz.mobileConnect.restrictDevices.steps.scan.started.${modeKey}`);
	}
	const description = (
		<div className={styles.restrictedDeviceDescription}>
			{i18nPackage.t(`beforeQuiz.mobileConnect.restrictDevices.description.${modeKey}.${statusKey}`)}
			<ol className={styles.steps}>
				{restrictDevice ? (
					<>
						<ConnectStep key="scan" index={0} status={scanStepStatus}>
							{scanStepText}
						</ConnectStep>
						<ConnectStep key="restrict" index={1} status={restrictStepStatus}>
							{restrictStepStatus === "completed"
								? i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.steps.restrict.completed")
								: i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.steps.restrict.started")}
						</ConnectStep>
					</>
				) : (
					<>
						<ConnectStep key="download" index={0} status="unstarted">
							{i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.steps.download")}
						</ConnectStep>
						<ConnectStep key="scan" index={1} status="unstarted">
							{scanStepText}
						</ConnectStep>
						<ConnectStep key="record" index={2} status="unstarted">
							{i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.steps.record")}
						</ConnectStep>
					</>
				)}
			</ol>
		</div>
	);

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.restrictedDeviceContainer}>
				<BasicSubstepHeader
					title={i18nPackage.t(`beforeQuiz.mobileConnect.restrictDevices.title.${modeKey}.${statusKey}`)}
					description={description}
					status={status}
				/>
				{status !== "completed" && (
					<div className={styles.qrWrapper}>
						<div className={styles.restrictedDeviceQRContainer}>
							<div className={styles.qrCornerTopLeft} />
							<div className={styles.qrCornerTopRight} />
							<div className={styles.qrCornerBottomLeft} />
							<div className={styles.qrCornerBottomRight} />
							{qrCodeUrl ? (
								<QRCodeCanvas value={qrCodeUrl} size={175} level="M" className={styles.qrImage} aria-label={i18nPackage.t(`beforeQuiz.mobileConnect.restrictDevices.steps.scan.started.${modeKey}`)} />
							) : (
								<Skeleton variant="rounded" animation="pulse" component="div" width={175} height={175} />
							)}
						</div>
						<Typography className={styles.restrictedDeviceQRDescription}>
							{i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.scanQRCode")}
						</Typography>
					</div>
				)}
			</div>
			{restrictDevice && status !== "completed" && <RestrictDeviceGuide />}
		</BasicSubstep>
	);
}

export default function MobileConnectStep({
	recordVideo = false,
	restrictDevice = false,
	children,
}: {
	recordVideo?: boolean;
	restrictDevice?: boolean;
	children: React.ReactNode;
}) {
	let modeKey: "record" | "restrict" | "restrictAndRecord";
	if (recordVideo) {
		modeKey = restrictDevice ? "restrictAndRecord" : "record";
	} else {
		modeKey = "restrict";
	}
	return (
		<BasicStep description={i18nPackage.t(`beforeQuiz.mobileConnect.description.${modeKey}`)}>{children}</BasicStep>
	);
}

// Trying to have text in one place
export const MOBILE_CONNECT_TEXT = {
	restrictDeviceFailToast: i18nPackage.t("beforeQuiz.mobileConnect.restrictDevices.failToast"),
};
